/**
 * Every clock on this console, in Kathmandu, assembled part by part.
 *
 * TWO BUGS, ONE CAUSE. `new Date(x).toLocaleString()` reads whatever locale
 * and timezone the runtime happens to have, and this console has two runtimes.
 * On `RevisionHistory` — the one Client Component that formats a date — the
 * server rendered `8/20/2026, 11:42:11 AM` and the browser hydrated
 * `20/08/2026, 11:42:11 am`, which is a hydration mismatch: React discards the
 * server tree and regenerates it, and Next's dev overlay has been reporting it
 * as an issue. Everywhere else it is quieter and worse — a Server Component
 * has no client render to disagree with, so it silently states a time in
 * whatever zone the pod is in.
 *
 * NPT AND NOT THE READER'S OWN ZONE. Every session, book closure and cutoff
 * this console reports is a NEPSE fact, and `embed/Stamp.tsx` already refuses
 * to read a weekday off the host clock for exactly that reason: a 15:00 +05:45
 * close is still Wednesday in Kathmandu at a reader's midnight west of UTC-9.
 * A console that timestamps a turn in the operator's zone and the market in
 * Nepal's is two clocks on one screen. The stamp rail at the top of every
 * admin page says `NPT` once, which is where the zone is declared; these
 * figures are read under it.
 *
 * ASSEMBLED FROM PARTS, NOT FROM A `dateStyle`. Two runtimes means two ICU
 * builds, and a locale's *pattern* is ICU data — "20 Aug 2026" and "20 Aug
 * 2026" are one CLDR release apart in ways nobody notices until the two halves
 * disagree again. Naming the parts is what makes the output a property of this
 * file rather than of whichever ICU each side shipped with. It is also what
 * pins the separators, so a figure looks the same in a screenshot from any
 * machine.
 */

const KATHMANDU = 'Asia/Kathmandu'

/**
 * The formatters, built once. `Intl.DateTimeFormat` is expensive to construct
 * and these render inside table rows.
 *
 * The locale is stated rather than left to the runtime for the same reason the
 * timezone is: it is the other half of what `toLocaleString()` was reading off
 * the host. Only the part *names* are taken from it here, so the choice is
 * narrow — `en-GB` gives `Aug` rather than `Aug.`, and 24-hour time, which is
 * what a log reads in.
 */
const DATE_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: KATHMANDU,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

/*
 * `hourCycle: "h23"` and not `hour12: false`, which is the last ICU-dependent
 * knob in a module whose whole argument is that the output must not depend on
 * which ICU each runtime shipped with. `hour12: false` does not name a cycle;
 * it asks the runtime to pick between `h23` (00-23) and `h24` (01-24), and
 * that mapping is CLDR data that has moved before. On a runtime resolving
 * `h24`, midnight in Kathmandu renders as `24:45` — and if only one of the two
 * runtimes moved, the result is exactly the hydration mismatch this file was
 * written to end.
 */
const TIME_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: KATHMANDU,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

/*
 * The stamp-rail formatter: the same instant, in the stamp's own voice.
 *
 * Built here rather than in the shell that draws it, which is where it was —
 * a second `Intl.DateTimeFormat` constructed on every render, assembling the
 * same parts by hand two files away from the module whose subject is that no
 * two places should format a date differently.
 *
 * It is DELIBERATELY not `nptDateTime`. A stamp is set in the widget's stamp
 * face — mono, uppercase, letter-spaced — so the month is upper-cased to match
 * the type around it, the seconds go (a chrome line that reads "when was this
 * page fetched" does not need them), and the zone is named once here because
 * this is the line the rest of the console's figures are read under.
 */
const STAMP_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: KATHMANDU,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? ''
}

/**
 * A date the engine sent, or the empty string if it sent something that is not
 * one.
 *
 * An unparseable timestamp returns nothing rather than `Invalid Date`, which
 * is the same call `parse_statement_date` makes on the engine side and for the
 * same reason: a cell that abstains is a fact an operator can act on, and one
 * reading `Invalid Date` is a bug report they have to write.
 */
export function nptDate(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const p = DATE_PARTS.formatToParts(at)
  return `${part(p, 'day')} ${part(p, 'month')} ${part(p, 'year')}`
}

/** The clock time alone, for a column whose rows all sit on one day. */
export function nptTime(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const p = TIME_PARTS.formatToParts(at)
  return `${part(p, 'hour')}:${part(p, 'minute')}:${part(p, 'second')}`
}

/** Both, for a row that stands on its own. */
export function nptDateTime(iso: string): string {
  const date = nptDate(iso)
  return date ? `${date}, ${nptTime(iso)}` : ''
}

/**
 * Date and clock to the MINUTE, for a card a reader looks at.
 *
 * `nptDateTime` keeps seconds and should: its callers are operator surfaces —
 * the guard feed, the revision history, a turn replay — where a second is how
 * two events are ordered. A market figure has no such second. A NEPSE close is
 * always :00, and the quote card drew "27 Aug 2026, 15:00:00", where the last
 * three characters are decoration on the most prominent line of the card.
 *
 * The engine's own §11 examples are minutes ("as of 14:32, live"), and
 * `nptStamp` already renders the rail that way, so this is the widget's two
 * surfaces agreeing rather than a new convention.
 */
export function nptDateMinute(iso: string): string {
  const date = nptDate(iso)
  if (!date) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const p = TIME_PARTS.formatToParts(at)
  return `${date}, ${part(p, 'hour')}:${part(p, 'minute')}`
}

/** The stamp rail's own line: `29 AUG 2026 · 19:47 NPT`. */
export function nptStamp(at: Date): string {
  const d = DATE_PARTS.formatToParts(at)
  const t = STAMP_TIME.formatToParts(at)
  const date = `${part(d, 'day')} ${part(d, 'month').toUpperCase()} ${part(d, 'year')}`
  return `${date} · ${part(t, 'hour')}:${part(t, 'minute')} NPT`
}
